"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ComponentPropsWithoutRef } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import type { StickToBottomContext } from "use-stick-to-bottom";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpRight,
    CircleSlash,
    Loader2,
    MessageSquare,
    Plus,
    Sparkles,
    Square,
} from "lucide-react";
import { cn } from "~/lib/utils";
import {
    Conversation,
    ConversationContent,
    ConversationBottomFade,
    ConversationScrollButton,
} from "~/components/ai-elements/conversation";
import {
    InlineCitation,
    InlineCitationCard,
    InlineCitationCardBody,
    InlineCitationCardTrigger,
    InlineCitationQuote,
    InlineCitationSource,
} from "~/components/ai-elements/inline-citation";
import {
    Message,
    MessageContent,
    MessageResponse,
} from "~/components/ai-elements/message";
import * as Analytics from "../analytics";
import { ASSET_CHAT_API_PATH, ASSET_CHAT_SUGGESTIONS } from "../consts";
import type {
    AssetChatCitation,
    AssetChatHistoryResponse,
    AssetChatStatus,
    ChatUIMessage,
} from "../types";

// // // //

type HydratedSession = {
    conversationId: string;
    initialMessages: ChatUIMessage[];
};

function createFreshSession(): HydratedSession {
    return {
        conversationId: crypto.randomUUID(),
        initialMessages: [],
    };
}

/**
 * Inline "Ask about this deal" chat, scoped to a single asset's document corpus.
 * Empty state matches the provided design (icon + grounded blurb + 2×2 starter
 * questions + input). Once a conversation starts, the transcript renders above the
 * input and the starter questions drop away.
 *
 * Answers are grounded server-side in the asset's dataroom documents. On mount this
 * hydrates the user's latest conversation for the asset via `GET {ASSET_CHAT_API_PATH}`
 * and continues it; if there's no history (or the endpoint isn't available) it starts a
 * fresh conversation. Pass `startFresh` to skip hydration and always begin an empty
 * conversation — nothing is persisted until the first message is submitted.
 *
 * Styling uses the screener's design tokens (border-muted / bg-card / primary /
 * *-foreground) so it reads as part of the expanded asset row.
 */
export function AssetChat(props: {
    /** Corpus asset id (screener symbol), e.g. "ACRED" — sent to the API as `assetId`. */
    corpusAssetId: string;
    /** Short display label used in the copy, e.g. the asset symbol ("ACRED"). */
    assetName: string;
    /**
     * Skip hydrating the asset's latest conversation and start an empty one.
     * The conversation is only persisted once the first message is submitted.
     */
    startFresh?: boolean;
    className?: string;
}) {
    const { corpusAssetId, assetName, startFresh = false, className } = props;
    const [session, setSession] = useState<HydratedSession | null>(null);
    const [isSessionBusy, setIsSessionBusy] = useState(false);

    const handleNewChat = useCallback(() => {
        if (isSessionBusy) {
            return;
        }
        setSession(createFreshSession());
    }, [isSessionBusy]);

    useEffect(() => {
        let cancelled = false;

        const hydrate = async () => {
            try {
                const response = await fetch(
                    `${ASSET_CHAT_API_PATH}?assetId=${encodeURIComponent(corpusAssetId)}`,
                    { headers: { accept: "application/json" } },
                );
                if (!response.ok) {
                    if (!cancelled) setSession(createFreshSession());
                    return;
                }
                const data =
                    (await response.json()) as AssetChatHistoryResponse;
                if (cancelled) return;
                setSession({
                    conversationId: data.conversationId ?? crypto.randomUUID(),
                    initialMessages: data.messages ?? [],
                });
            } catch {
                if (!cancelled) setSession(createFreshSession());
            }
        };

        setIsSessionBusy(false);
        if (startFresh) {
            // Begin an empty conversation, no network call. Persistence still
            // waits for the first submitted message (via the POST route).
            setSession(createFreshSession());
        } else {
            setSession(null);
            void hydrate();
        }
        return () => {
            cancelled = true;
        };
    }, [corpusAssetId, startFresh]);

    return (
        <div
            className={cn(
                "flex flex-col gap-5 rounded-2xl bg-card p-4 shadow-lg shadow-black/5 ring-1 ring-black/5 sm:p-5 dark:ring-white/10",
                className,
            )}
        >
            <ChatHeader
                assetName={assetName}
                onNewChat={handleNewChat}
                disabled={!session || isSessionBusy}
            />
            {session ? (
                <AssetChatSession
                    key={session.conversationId}
                    corpusAssetId={corpusAssetId}
                    assetName={assetName}
                    conversationId={session.conversationId}
                    initialMessages={session.initialMessages}
                    onBusyChange={setIsSessionBusy}
                />
            ) : (
                <div className="flex items-center justify-center py-8 text-tertiary-foreground">
                    <Loader2
                        className="size-5 animate-spin"
                        aria-hidden="true"
                    />
                </div>
            )}
        </div>
    );
}

// // // //

function ChatHeader(props: {
    assetName: string;
    onNewChat: () => void;
    disabled: boolean;
}) {
    return (
        <div className="flex gap-3">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-primary to-primary-dark text-primary-foreground shadow-sm shadow-primary/25 ring-1 ring-inset ring-white/15">
                <Sparkles className="size-5" aria-hidden="true" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex items-start justify-between gap-3">
                    <h3 className="min-w-0 text-base font-semibold text-foreground">
                        Ask about this deal
                    </h3>
                    <button
                        type="button"
                        onClick={props.onNewChat}
                        disabled={props.disabled}
                        aria-label="Start a new chat"
                        title="Start a new chat"
                        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-muted bg-card px-2.5 text-xs font-medium text-secondary-foreground shadow-sm transition hover:border-primary-light hover:bg-primary-light/30 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-muted disabled:hover:bg-card disabled:hover:text-secondary-foreground"
                    >
                        <Plus className="size-3.5" aria-hidden="true" />
                        <span>New chat</span>
                    </button>
                </div>
                <p className="text-sm leading-5 text-tertiary-foreground">
                    Answers are drawn only from {props.assetName}&apos;s
                    documents. This AI does not use the internet.
                </p>
            </div>
        </div>
    );
}

// // // //

/**
 * Compact markdown styling for the assistant's answers. Streamdown ships large
 * document-scale defaults (h1 `text-3xl`, `list-inside` lists, etc.) that dwarf an
 * inline chat; these descendant overrides scale the type down to a tight, readable
 * chat rhythm. Works because the project sets Tailwind `important: true`, so these
 * utilities win over Streamdown's own classes.
 */
export const ASSISTANT_MARKDOWN = cn(
    "text-sm font-light leading-[1.4] text-foreground",
    // Headings — compact 18/16/14 hierarchy instead of 30/24/20.
    "[&_h1]:mt-6 [&_h1]:mb-3 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:tracking-tight [&_h1]:text-foreground",
    "[&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-foreground",
    "[&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-foreground",
    "[&_h4]:mt-4 [&_h4]:mb-1 [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-secondary-foreground",
    // Paragraphs.
    "[&_p]:my-3 [&_p]:leading-[1.4]",
    // Lists — hanging markers (outside) read cleaner than Streamdown's list-inside.
    "[&_ul]:my-3 [&_ul]:list-outside [&_ul]:list-disc [&_ul]:pl-5",
    "[&_ol]:my-3 [&_ol]:list-outside [&_ol]:list-decimal [&_ol]:pl-5",
    "[&_li]:my-1 [&_li]:pl-1 [&_li]:leading-[1.4] [&_li]:marker:text-muted-foreground",
    "[&_ul_ul]:my-1 [&_ol_ol]:my-1 [&_ul_ol]:my-1 [&_ol_ul]:my-1",
    // Emphasis + links.
    "[&_strong]:font-semibold [&_strong]:text-foreground",
    "[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-primary/40 [&_a:hover]:decoration-primary",
    // Rules + quotes.
    "[&_hr]:my-5 [&_hr]:border-muted",
    "[&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-primary/30 [&_blockquote]:pl-3 [&_blockquote]:text-secondary-foreground [&_blockquote]:not-italic",
    // Trim outer margins so the bubble hugs the content.
    "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
);

/**
 * Word-level fade-in for streamed answer text, modeled on how claude.ai paces
 * its reveal: batched updates (see `experimental_throttle` on `useChat`) land
 * as word groups that fade in with a short stagger, instead of tokens popping
 * in one by one. Streamdown tracks previously rendered characters, so text
 * that is already visible never re-animates — only the new tail of each batch
 * fades in. Tune together with the reveal pacing (SMOOTH_TICK_MS): the stagger
 * cascade of one batch should roughly finish by the time the next tick lands.
 */
const STREAMING_TEXT_ANIMATION = {
    animation: "fadeIn",
    duration: 250,
    easing: "ease-out",
    sep: "word",
    stagger: 15,
} as const;

// Reveal-pacing tunables for useSmoothedText. Each tick releases
// SMOOTH_CATCH_UP of the undisplayed backlog, so a steady stream runs
// ~2-3 ticks (~300ms) behind the wire and a burst (e.g. the flush after a
// tool call) spreads out over ~1s instead of popping in as a wall of text.
const SMOOTH_TICK_MS = 120;
const SMOOTH_CATCH_UP = 0.35;
const SMOOTH_MIN_CHARS_PER_TICK = 4;
const SMOOTH_MAX_CHARS_PER_TICK = 120;

/** Latches true once `isStreaming` has been true during this mount. */
function useHasStreamed(isStreaming: boolean): boolean {
    const hasStreamedRef = useRef(isStreaming);
    if (isStreaming) {
        hasStreamedRef.current = true;
    }
    return hasStreamedRef.current;
}

/**
 * Meters streamed text onto the screen at a steady pace, modeled on
 * claude.ai's stream smoother. Raw `useChat` updates render whatever the
 * network delivered since the last update — chaotic pacing on jittery
 * connections and paragraph-sized pops after stalls. This hook buffers the
 * incoming text and reveals a prefix that grows by a fraction of the backlog
 * per tick, cut at a word boundary so words land whole for the word fade-in
 * (STREAMING_TEXT_ANIMATION). When the stream closes with text still
 * unrevealed, the reveal keeps ticking until the backlog drains, so the tail
 * glides in instead of jump-cutting the moment the chat unlocks.
 */
function useSmoothedText(text: string, isStreaming: boolean): string {
    // Start caught-up: remounts (e.g. reopening the dock) and historical
    // messages must not replay the reveal.
    const [visibleLength, setVisibleLength] = useState(text.length);
    const visibleRef = useRef(visibleLength);
    const textRef = useRef(text);
    textRef.current = text;
    const hasStreamed = useHasStreamed(isStreaming);
    const isRevealing =
        isStreaming || (hasStreamed && visibleLength < text.length);

    useEffect(() => {
        if (!isRevealing) return;
        const interval = setInterval(() => {
            const target = textRef.current.length;
            if (visibleRef.current > target) {
                // Text got shorter — the message was replaced (regenerate).
                visibleRef.current = target;
                setVisibleLength(target);
                return;
            }
            const backlog = target - visibleRef.current;
            if (backlog === 0) {
                return;
            }
            const step = Math.min(
                SMOOTH_MAX_CHARS_PER_TICK,
                Math.max(
                    SMOOTH_MIN_CHARS_PER_TICK,
                    Math.round(backlog * SMOOTH_CATCH_UP),
                ),
            );
            visibleRef.current = Math.min(target, visibleRef.current + step);
            setVisibleLength(visibleRef.current);
        }, SMOOTH_TICK_MS);
        return () => clearInterval(interval);
    }, [isRevealing]);

    if (!isRevealing) {
        return text;
    }
    const revealed = text.slice(0, Math.min(visibleLength, text.length));
    if (!isStreaming) {
        // Draining after the stream closed: no more text is coming, so the
        // partial-word holdback below would only strand the final word.
        return revealed;
    }
    // Hold back the trailing partial word: it would otherwise flicker between
    // shown (while caught up) and held (while behind), and re-trigger its
    // fade-in as it grows.
    const wholeWords = revealed.replace(/\S+$/, "");
    return wholeWords.length > 0 ? wholeWords : revealed;
}

const CITATION_HREF_PREFIX = "#asset-citation-";
// Must be checked before CITATION_HREF_PREFIX (which is its prefix).
const PENDING_CITATION_HREF_PREFIX = "#asset-citation-pending-";

function getCitationMarkdown(
    text: string,
    citationsByNumber: Map<string, AssetChatCitation>,
    isStreaming: boolean,
): string {
    return text.replace(/\[(\d{1,3})\]/g, (_marker, number: string) => {
        if (citationsByNumber.has(number)) {
            return `[\\[${number}\\]](${CITATION_HREF_PREFIX}${number})`;
        }
        // While streaming, keep unresolved markers in place as muted
        // placeholders — their citations often arrive only in the repair pass,
        // and stripping-then-reinserting reflows the finished answer. Markers
        // that never resolve are dropped once generation ends.
        return isStreaming
            ? `[\\[${number}\\]](${PENDING_CITATION_HREF_PREFIX}${number})`
            : "";
    });
}

function getMessageCitations(message: ChatUIMessage): AssetChatCitation[] {
    return message.parts.flatMap((part) =>
        part.type === "data-citation" ? [part.data] : [],
    );
}

/** Latest generation status streamed by the route (see AssetChatStatus). */
export function getGenerationStatus(
    message: ChatUIMessage,
): AssetChatStatus | undefined {
    for (const part of message.parts) {
        if (part.type === "data-status") {
            return part.data;
        }
    }
    return undefined;
}

function hasMessageText(message: ChatUIMessage | undefined): boolean {
    return (
        message?.parts.some(
            (part) => part.type === "text" && part.text.trim().length > 0,
        ) ?? false
    );
}

function SourceExcerpt(props: { citation: AssetChatCitation }) {
    const { citation } = props;
    const legacyQuote = citation.quote
        ? `${citation.quote.slice(0, 420).trim()}${citation.quote.length > 420 ? "…" : ""}`
        : undefined;
    const excerpt = citation.sourceExcerpt ?? legacyQuote;
    const quote = citation.quote;
    if (!excerpt) {
        return null;
    }

    if (!quote) {
        return <InlineCitationQuote>“{excerpt}”</InlineCitationQuote>;
    }

    const matchIndex = excerpt.toLowerCase().indexOf(quote.toLowerCase());
    if (matchIndex === -1) {
        return <InlineCitationQuote>“{excerpt}”</InlineCitationQuote>;
    }

    const before = excerpt.slice(0, matchIndex);
    const match = excerpt.slice(matchIndex, matchIndex + quote.length);
    const after = excerpt.slice(matchIndex + quote.length);

    return (
        <InlineCitationQuote className="max-h-56 overflow-y-auto pr-1">
            {"“"}
            {before}
            <strong className="font-semibold text-foreground not-italic">
                {match}
            </strong>
            {after}
            {"”"}
        </InlineCitationQuote>
    );
}

/**
 * Inline citation chip. While the marker's `cite` data hasn't streamed in yet
 * (the verification pass), the chip renders dimmed and its hover popover shows
 * a "Verifying citation…" spinner. Once `citation` arrives, the same popover
 * swaps to the source card — same component, same tree position, so an open
 * popup morphs in place instead of closing and reopening.
 */
function CitationMarker(props: {
    number: string;
    citation?: AssetChatCitation;
}) {
    const { number, citation } = props;

    return (
        <InlineCitation>
            <InlineCitationCard>
                <InlineCitationCardTrigger
                    aria-label={
                        citation
                            ? `View source ${number}: ${citation.title}`
                            : `Source ${number} is being verified`
                    }
                    className={
                        citation
                            ? undefined
                            : "bg-muted text-muted-foreground opacity-60 hover:bg-muted"
                    }
                    sources={citation ? [citation.title] : []}
                >
                    {number}
                </InlineCitationCardTrigger>
                <InlineCitationCardBody
                    align="start"
                    // The card portals to <body>; raise it above the GlobalChatDock
                    // panel (z-[110]) so it isn't clipped behind the dock.
                    className={cn(
                        "z-[120]",
                        citation ? "w-96 max-w-[calc(100vw-2rem)]" : "w-auto",
                    )}
                >
                    {citation ? (
                        <div className="space-y-2 p-3">
                            <InlineCitationSource title={citation.title} />
                            <SourceExcerpt citation={citation} />
                        </div>
                    ) : (
                        <div
                            role="status"
                            className="flex items-center gap-2 px-3 py-2 text-xs text-tertiary-foreground"
                        >
                            <Loader2
                                className="size-3 animate-spin text-primary"
                                aria-hidden="true"
                            />
                            Verifying citation…
                        </div>
                    )}
                </InlineCitationCardBody>
            </InlineCitationCard>
        </InlineCitation>
    );
}

export function AssetChatResponse(props: {
    text: string;
    citations: AssetChatCitation[];
    /**
     * True while this answer is still generating — shows the streaming caret
     * and renders unresolved `[n]` markers as pending placeholders instead of
     * stripping them.
     */
    isStreaming?: boolean;
}) {
    const isStreaming = props.isStreaming ?? false;
    // `animated` stays latched on after this answer finishes: flipping it off
    // would make Streamdown unwrap every word's animation span — a full DOM
    // swap of the answer right as the composer unlocks. Streamdown never
    // re-animates characters it has already rendered, so leaving it on is
    // visually inert, while historical messages (never streamed while mounted)
    // still render plain and skip the cascade.
    const hasStreamed = useHasStreamed(isStreaming);
    const smoothedText = useSmoothedText(props.text, isStreaming);
    const citationsByNumber = useMemo(
        () =>
            new Map(
                props.citations.map((citation) => [citation.number, citation]),
            ),
        [props.citations],
    );
    const markdown = useMemo(
        () => getCitationMarkdown(smoothedText, citationsByNumber, isStreaming),
        [citationsByNumber, smoothedText, isStreaming],
    );
    const components = useMemo(
        () => ({
            a: (
                componentProps: ComponentPropsWithoutRef<"a"> & {
                    node?: unknown;
                },
            ) => {
                const href = componentProps.href;
                if (
                    typeof href === "string" &&
                    href.startsWith(PENDING_CITATION_HREF_PREFIX)
                ) {
                    return (
                        <CitationMarker
                            number={href.slice(
                                PENDING_CITATION_HREF_PREFIX.length,
                            )}
                        />
                    );
                }
                const number =
                    typeof href === "string" &&
                    href.startsWith(CITATION_HREF_PREFIX)
                        ? href.slice(CITATION_HREF_PREFIX.length)
                        : undefined;
                const citation =
                    number !== undefined
                        ? citationsByNumber.get(number)
                        : undefined;

                if (!citation) {
                    const { node: _node, ...anchorProps } = componentProps;
                    return <a {...anchorProps} />;
                }

                return (
                    <CitationMarker
                        number={citation.number}
                        citation={citation}
                    />
                );
            },
        }),
        [citationsByNumber],
    );

    return (
        <MessageResponse
            className={ASSISTANT_MARKDOWN}
            components={components}
            // Streamdown's blinking caret rides the newest token, keeping the
            // answer visibly alive through mid-answer tool-call pauses and the
            // post-stream backlog drain (smoothedText still catching up).
            isAnimating={isStreaming || smoothedText !== props.text}
            caret="block"
            animated={hasStreamed ? STREAMING_TEXT_ANIMATION : undefined}
        >
            {markdown}
        </MessageResponse>
    );
}

/**
 * Status line shown while the post-answer citation repair pass runs — the
 * answer looks finished, but source chips are still being verified.
 */
export function VerifyingCitationsNote() {
    return (
        <span
            role="status"
            className="flex items-center gap-1.5 text-xs text-tertiary-foreground"
        >
            <Loader2
                className="size-3 animate-spin text-primary"
                aria-hidden="true"
            />
            Verifying citations…
        </span>
    );
}

/** Marks a stopped or errored partial answer so it isn't read as complete. */
export function InterruptedNote() {
    return (
        <span className="flex items-center gap-1.5 text-xs text-tertiary-foreground italic">
            <CircleSlash className="size-3" aria-hidden="true" />
            Response interrupted
        </span>
    );
}

function AssetChatLoadingResponse() {
    return (
        <Message from="assistant">
            <MessageContent
                role="status"
                aria-live="polite"
                className="rounded-lg border border-muted bg-card px-4 py-3 text-tertiary-foreground shadow-sm"
            >
                <div className="flex items-center gap-2">
                    <Loader2
                        className="size-4 animate-spin text-primary"
                        aria-hidden="true"
                    />
                    <span>Reading the documents…</span>
                </div>
                <div className="flex flex-col gap-1.5" aria-hidden="true">
                    <div className="h-2 w-52 max-w-full animate-pulse rounded-full bg-muted" />
                    <div className="h-2 w-32 max-w-full animate-pulse rounded-full bg-muted/80" />
                </div>
            </MessageContent>
        </Message>
    );
}

// // // //

function AssetChatSession(props: {
    corpusAssetId: string;
    assetName: string;
    conversationId: string;
    initialMessages: ChatUIMessage[];
    onBusyChange: (isBusy: boolean) => void;
}) {
    const {
        corpusAssetId,
        assetName,
        conversationId,
        initialMessages,
        onBusyChange,
    } = props;
    const [input, setInput] = useState("");
    // Whether the user stopped the in-flight generation (marks the partial).
    const [wasStopped, setWasStopped] = useState(false);
    const conversationContextRef = useRef<StickToBottomContext | null>(null);

    const transport = useMemo(
        () =>
            new DefaultChatTransport({
                api: ASSET_CHAT_API_PATH,
                body: { assetId: corpusAssetId },
            }),
        [corpusAssetId],
    );

    const { messages, sendMessage, status, regenerate, stop } =
        useChat<ChatUIMessage>({
            id: conversationId,
            messages: initialMessages,
            transport,
            // Batch stream updates into word-group chunks (~6-7/s) that each
            // fade in via STREAMING_TEXT_ANIMATION, instead of re-rendering on
            // every token. Also caps markdown re-parses.
            experimental_throttle: 150,
        });

    const isBusy = status === "submitted" || status === "streaming";
    const hasMessages = messages.length > 0;
    const isError = status === "error";
    const lastMessage = messages[messages.length - 1];
    const isWaitingForReply =
        isBusy &&
        (lastMessage?.role === "user" ||
            (lastMessage?.role === "assistant" &&
                !hasMessageText(lastMessage)));
    // The last answer is a partial: stopped by the user or cut off by an error.
    const interruptedMessageId =
        !isBusy &&
        (wasStopped || isError) &&
        lastMessage?.role === "assistant" &&
        hasMessageText(lastMessage)
            ? lastMessage.id
            : null;

    useEffect(() => {
        onBusyChange(isBusy);

        return () => onBusyChange(false);
    }, [isBusy, onBusyChange]);

    const scrollTranscriptToBottom = useCallback(
        (behavior: ScrollBehavior = "instant") => {
            const scrollElement =
                conversationContextRef.current?.scrollRef.current;
            if (scrollElement) {
                scrollElement.scrollTo({
                    top: scrollElement.scrollHeight,
                    behavior,
                });
                return;
            }

            void conversationContextRef.current?.scrollToBottom(behavior);
        },
        [],
    );

    const queueTranscriptScrollToBottom = useCallback(() => {
        requestAnimationFrame(() => {
            scrollTranscriptToBottom();
            requestAnimationFrame(() => scrollTranscriptToBottom());
        });
    }, [scrollTranscriptToBottom]);

    useEffect(() => {
        if (!isBusy) {
            return;
        }

        queueTranscriptScrollToBottom();
    }, [isBusy, messages.length, queueTranscriptScrollToBottom]);

    const submit = (text: string, source: "typed" | "suggestion") => {
        const trimmed = text.trim();
        if (!trimmed || isBusy) {
            return;
        }
        Analytics.trackSendMessage({
            message: trimmed,
            assetCorpusId: corpusAssetId,
            conversationId,
            surface: "inline",
            source,
            isFirstMessage: messages.length === 0,
        });
        setWasStopped(false);
        void sendMessage({ text: trimmed });
        setInput("");
        queueTranscriptScrollToBottom();
    };

    const handleStop = () => {
        Analytics.trackStopGeneration({
            assetCorpusId: corpusAssetId,
            conversationId,
            surface: "inline",
        });
        setWasStopped(true);
        void stop();
    };

    // Track answers that end in the error banner.
    useEffect(() => {
        if (isError) {
            Analytics.trackResponseError({
                assetCorpusId: corpusAssetId,
                conversationId,
                surface: "inline",
            });
        }
    }, [isError, corpusAssetId, conversationId]);

    return (
        <>
            {/* Transcript (once a conversation has started) */}
            {hasMessages && (
                <Conversation
                    contextRef={conversationContextRef}
                    className="rounded-lg border border-muted bg-muted/20"
                >
                    <ConversationContent
                        scrollClassName="max-h-96 overflow-y-auto"
                        // pb clears the h-12 ConversationBottomFade so streamed
                        // text never renders under the gradient.
                        className="gap-5 pb-14"
                    >
                        {messages.map((message, messageIndex) => {
                            const isActiveAssistant =
                                isBusy &&
                                message.role === "assistant" &&
                                messageIndex === messages.length - 1;
                            return (
                                <Message key={message.id} from={message.role}>
                                    <MessageContent className="group-[.is-user]:bg-primary-light/40 group-[.is-user]:text-foreground">
                                        {message.parts.map((part, index) => {
                                            if (part.type !== "text") {
                                                return null;
                                            }

                                            return (
                                                <AssetChatResponse
                                                    key={`${message.id}-${index}`}
                                                    text={part.text}
                                                    citations={getMessageCitations(
                                                        message,
                                                    )}
                                                    isStreaming={
                                                        isActiveAssistant
                                                    }
                                                />
                                            );
                                        })}
                                        {isActiveAssistant &&
                                            getGenerationStatus(message)
                                                ?.state ===
                                                "verifying-citations" && (
                                                <VerifyingCitationsNote />
                                            )}
                                        {message.id ===
                                            interruptedMessageId && (
                                            <InterruptedNote />
                                        )}
                                    </MessageContent>
                                </Message>
                            );
                        })}
                        {isWaitingForReply && <AssetChatLoadingResponse />}
                    </ConversationContent>
                    <ConversationBottomFade />
                    {/* "Latest" pill: jump back to the newest message. */}
                    <ConversationScrollButton
                        size="sm"
                        className="bottom-3 h-7 gap-1 border-muted-foreground/60 bg-card px-3 text-xs font-medium shadow-md"
                    >
                        <ArrowDown className="size-3.5" aria-hidden="true" />
                        Latest
                    </ConversationScrollButton>
                </Conversation>
            )}

            {/* Error */}
            {isError && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    <span>Something went wrong. Please try again.</span>
                    <button
                        type="button"
                        onClick={() => void regenerate()}
                        className="shrink-0 font-medium underline underline-offset-2 hover:opacity-80"
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Starter questions (empty state only) */}
            {!hasMessages && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {ASSET_CHAT_SUGGESTIONS.map((question) => (
                        <button
                            key={question}
                            type="button"
                            onClick={() => submit(question, "suggestion")}
                            disabled={isBusy}
                            className="group flex cursor-pointer items-center gap-2.5 rounded-lg border border-transparent bg-muted/40 py-3 pr-3 pl-4 text-left text-sm font-medium text-secondary-foreground transition hover:border-primary-light hover:bg-primary-light/40 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <MessageSquare
                                className="size-4 shrink-0 text-primary"
                                aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1 truncate">
                                {question}
                            </span>
                            <ArrowUpRight
                                className="size-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary"
                                aria-hidden="true"
                            />
                        </button>
                    ))}
                </div>
            )}

            {/* Input */}
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    submit(input, "typed");
                }}
                className="flex items-center gap-2.5 rounded-lg border border-muted bg-card py-2 pr-2 pl-4 transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15"
            >
                <MessageSquare
                    className="size-4 shrink-0 text-tertiary-foreground"
                    aria-hidden="true"
                />
                <input
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    placeholder={`Ask anything about ${assetName}...`}
                    className="min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-foreground shadow-none placeholder:text-tertiary-foreground focus:border-0 focus:outline-none focus:ring-0"
                    aria-label={`Ask anything about ${assetName}`}
                />
                {isBusy ? (
                    <button
                        type="button"
                        onClick={handleStop}
                        aria-label="Stop generating"
                        title="Stop generating"
                        className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm shadow-primary/25 transition-colors hover:bg-primary-dark"
                    >
                        <Square
                            className="size-3 fill-current"
                            aria-hidden="true"
                        />
                    </button>
                ) : (
                    <button
                        type="submit"
                        disabled={!input.trim()}
                        aria-label="Send"
                        className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm shadow-primary/25 transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:hover:bg-primary"
                    >
                        <ArrowUp className="size-4" aria-hidden="true" />
                    </button>
                )}
            </form>
        </>
    );
}
